import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { takeUntil, first } from "rxjs/operators";
import { Subject } from "rxjs";

import { AuthenticationService, UserService } from "app/auth/service";
import { CoreConfigService } from "@core/services/config.service";
import Swal from "sweetalert2";
import { ToastrService } from "ngx-toastr";

@Component({
  selector: "app-auth-first-access",
  templateUrl: "./auth-first-access.component.html",
  styleUrls: ["./auth-first-access.component.scss"],
})
export class AuthFirstAccessComponent implements OnInit {
  //  Public
  public coreConfig: any;
  public loginForm: UntypedFormGroup;
  public loading = false;
  public submitted = false;
  public returnUrl: string;
  public error = "";
  public passwordTextType: boolean;

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {CoreConfigService} _coreConfigService
   * @param {UntypedFormBuilder} _formBuilder
   * @param {ActivatedRoute} _route
   * @param {Router} _router
   * @param {AuthenticationService} _authenticationService
   * @param {UserService} _userService
   * @param {ToastrService} _toastrService
   *
   */
  constructor(
    private _coreConfigService: CoreConfigService,
    private _formBuilder: UntypedFormBuilder,
    private _route: ActivatedRoute,
    private _router: Router,
    private _authenticationService: AuthenticationService,
    private _userService: UserService,
    private _toastrService: ToastrService
  ) {
    // redirect to home if already logged in
    if (this._authenticationService.currentUserValue) {
      this._router.navigate(["/"]);
    }

    this._unsubscribeAll = new Subject();

    // Configure the layout
    this._coreConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        menu: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        customizer: false,
        enableLocalStorage: false,
      },
    };
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  /**
   * Toggle password
   */
  togglePasswordTextType() {
    this.passwordTextType = !this.passwordTextType;
  }

  askForCode() {
    Swal.fire({
      icon: "info",
      title: "Confirmation Code",
      html: `<div><p>Type the confirmation code sent to your email.</p></div><div><small class='text-muted'>This code expires in 02:00 minutes</small></div>`,
      input: "text",
      timer: 120000,
      timerProgressBar: true,
      showCloseButton: true,
      allowEscapeKey: true,
      allowOutsideClick: false,
      confirmButtonText: "Confirm",
      cancelButtonText: "Resend",
      showCancelButton: true,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary ml-1",
      },
    }).then((data) => {
      if (data.isConfirmed) {
        this._userService
          .validateFirstAccess(
            this.f.email.value,
            this.f.password.value,
            data.value
          )
          .then(
            (response) => {
              this._authenticationService.authenticateUser(response);
              this._router.navigate(["/apps/onboarding/user-onboarding"]);
            },
            (err) => {
              this.error = err;
              this.loading = false;
            }
          );
      } else if (
        data.dismiss === Swal.DismissReason.close ||
        data.dismiss === Swal.DismissReason.esc
      ) {
        this.loading = false;
      } else {
        this.askForCode();
      }
    });
  }

  onSubmit() {
    this.submitted = true;

    // stop here if form is invalid
    if (this.loginForm.invalid) {
      return;
    }

    const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let timeZone = currentTimeZone;

    // Login
    this.loading = true;
    this._userService
      .createNewUserFirstAccess(
        this.f.firstName.value,
        this.f.lastName.value,
        this.f.email.value,
        timeZone
      )
      .then(
        (data) => {
          if (!data["result"]) {
            this.loading = false;
            this.error = "Email is already in use";
          } else {
            this.askForCode();
          }
          //
        },
        (error) => {
          this.error = error;
          this.loading = false;
        }
      );
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this.loginForm = this._formBuilder.group({
      email: ["", [Validators.required, Validators.email]],
      firstName: ["", [Validators.required]],
      lastName: ["", [Validators.required]],
      password: ["", [Validators.required, Validators.minLength(10)]],
    });

    // get return url from route parameters or default to '/'
    this.returnUrl = this._route.snapshot.queryParams["returnUrl"] || "/";

    // Subscribe to config changes
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.coreConfig = config;
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
